// Assembles the site copy.
//
// Every translatable string lives in src/locales/<lang>.json -- one file per
// language, so a translator can be handed a single self-contained file. This
// module joins those files to the language-neutral product data below (images
// and the shared diameter table), which must stay identical across languages
// and so is deliberately kept out of the locale files.

import en from './locales/en.json';
import da from './locales/da.json';
import de from './locales/de.json';
import fr from './locales/fr.json';
import es from './locales/es.json';
import { LANGS } from './routes';

const LOCALES = { en, da, de, fr, es };

export { LANGS };

// Display order of the flag column, chosen by the owner.
export const LANG_FLAGS = [
  { code: 'da', label: 'Dansk', flag: 'Flag of Denmark.png' },
  { code: 'de', label: 'Deutsch', flag: 'Flag of Germany.svg' },
  { code: 'en', label: 'English', flag: 'Flag of the United Kingdom.png' },
  { code: 'fr', label: 'Français', flag: 'Flag of France.png' },
  { code: 'es', label: 'Español', flag: 'Flag of Spain.png' },
];

// ───────────────────────── LANGUAGE-NEUTRAL PRODUCT DATA ─────────────────────
// Each product carries a `gallery`: every picture for that product from the
// original site, so the expanded card can cycle through all of them. `thumb`
// is the collapsed-card image (the first gallery picture).
const PRODUCT_IMAGES = {
  'cable-joint': {
    gallery: [
      'cablejoint 1_medium.jpg',
      'RE 325 i sand og vand-small 005.jpg',
      'P9070065.jpg',
      'cablejoint.gif',
      'cablejoint3.gif',
    ],
  },
  'flush-mounted-cable-gland': {
    gallery: [
      'flush-mounted cable gland 1_medium.jpg',
      'flush-mounted cable gland 2_medium.jpg',
      'flush-mounted cable gland 3_medium.jpg',
      '012_b_medium.gif',
      'flushmountedcablegland.gif',
    ],
  },
  'cord-grip-bush': {
    gallery: [
      'cord grip bush 1_medium.jpg',
      'cord grip bush 2_medium.jpg',
      'cord grip bush 3_medium.jpg',
      '003 1_medium.jpg',
      '004 1_medium.jpg',
      '009 1_medium.jpg',
      '009 3_medium.jpg',
      '009 4_medium.jpg',
      '003_b_medium.gif',
      '004_b_medium.gif',
      '009_b_medium.gif',
      'cordGribBush2016.jpg',
      'cordgripbush.gif',
    ],
  },
  'cable-gland-dome-top': {
    gallery: [
      'cable gland with dome top 1_medium.jpg',
      'cable_gland 1_medium.jpg',
      '009 2_medium.jpg',
      '010 1_medium.jpg',
      '010 2_medium.jpg',
      '010_b_medium.gif',
      'cable_gland_with_dome_top.gif',
      'cable_gland_with_dome_top_seperated.gif',
    ],
  },
  'pg-skid-washer': {
    gallery: [
      'pg-skid washer 1_medium.jpg',
      '007_medium.gif',
      'pg-skid_washer.gif',
    ],
  },
  'o-ring': {
    gallery: [
      'o-rings 1_medium.jpg',
      '003 2_medium.jpg',
      '003 3_medium.jpg',
      'o-rings.gif',
    ],
  },
};

// Cord Grip Bush diameter ranges — numeric, identical in every language, so
// they live here rather than being duplicated (and risking drift) per locale.
const CORD_GRIP_ORDERING = [
  ['Pg9 / M16', '3.6 - 11.3 mm'],
  ['Pg11', '4.0 - 13.3 mm'],
  ['Pg13.5 / M20', '4.1 - 14.3 mm'],
  ['Pg16', '5.0 - 16.2 mm'],
  ['Pg21', '5.9 - 21.2 mm'],
  ['M25', '- 15.8 mm'],
  ['Pg29', '7.1 - 29.0 mm'],
  ['M32', '- 21.9 mm'],
];

// Products whose ordering table is numbers only, and so is shared rather than
// translated. Products with prose in the table keep it in their locale file.
const SHARED_ORDERING = { 'cord-grip-bush': CORD_GRIP_ORDERING };

// Attach the shared data to each translated item by id. `...item` comes last
// so a locale file always wins over the defaults for anything it does define.
const hydrate = (items) =>
  items.map((item) => {
    const images = PRODUCT_IMAGES[item.id];
    const ordering = SHARED_ORDERING[item.id];
    return {
      thumb: images.gallery[0],
      ...images,
      ...(ordering ? { ordering } : {}),
      ...item,
    };
  });

const byLang = (pick) =>
  Object.fromEntries(LANGS.map((lang) => [lang, pick(LOCALES[lang])]));

export const NAV = byLang((l) => l.nav);
export const META = byLang((l) => l.meta);

export const content = byLang(({ home, about, contact }) => ({
  home,
  about,
  contact,
}));

export const products = byLang(({ products: p }) => ({
  labels: p.labels,
  items: hydrate(p.items),
}));
